/**
 * 🙌 **Picking a sheep up, and what that makes it** — T261, D123, D117.
 *
 * 🎯 **haugur, 2026-09-23:** *"Unless in a pen then no, they wander, **you can
 * pick them up and put them in a pen if you find them in the wild**."*
 *
 * ## 🔴 CARRYING IS NOT A CONVENIENCE. IT IS THE ONLY WAY TO MOVE A SHEEP.
 *
 * **Minecraft moves an animal with a LEAD, and a lead is made of STRING.**
 * ⚠️ **This game has no string, no rope and no flax** — that is exactly why
 * **D115** chose the spear over a fishing rod — **and the first string there
 * will ever be is spun from WOOL, which comes off the sheep you cannot yet
 * move.**
 *
 * 👉 ***So a lead is impossible before the first sheep, by construction.*** His
 * answer is not a simplification of Minecraft; **it is the only thing that works
 * here.**
 *
 * ## 🔴 AND PICKING ONE UP IS WHAT MAKES IT YOURS — which is the whole of T261
 *
 * 📖 **`Minecraft/06_Mobs.md` §5 is the source, and it calls this the best idea
 * in the document:** instead of a curated list of protected creatures, **the
 * game infers value from PLAYER ACTION.** *"You do not declare a cow important;
 * you show it, by feeding it."* **It costs one boolean.**
 *
 * ✅ **So: a sheep you have carried is a sheep you have invested in, and it
 * becomes {@link Creature.kept}** — written to the database, never forgotten at
 * distance, and **alive after a restart.** ⚠️ **Everything else stays ambient**:
 * spawned near whoever is standing about, forgotten when they leave, never
 * written down. **That is correct for an animal nobody owns.**
 *
 * 👉 ***A sheep nobody owns is a sheep nobody can lose, and losing them is the
 * whole point of keeping them.***
 */

import { type AimTarget, aimAt } from './aim.ts';
import { SHEEP_HEIGHT, SHEEP_LENGTH } from './sheepmind.ts';
import type { Vec3 } from './creature.ts';

/**
 * 🙌 **How far you can reach to pick something up, in metres.**
 *
 * 🔴 **ARM'S LENGTH, AND SHORTER THAN THE SPEAR'S 3.5 m ON PURPOSE.** A spear
 * is a stick with reach; hands are not. ⚠️ **It is also shorter than the 6 m
 * block reach**, so *"I can build there"* never means *"I can pick that up"* —
 * **two different verbs must not share one distance**, or changing either
 * silently changes the other.
 *
 * ⚠️ **A DIAL.** If it reads as fiddly, this is the number to move, and moving
 * it moves both ends at once because both import it.
 */
export const CARRY_REACH = 2.5;

/**
 * 🐑 **Which species may be picked up at all, and how big a target each is.**
 *
 * 🔴 **A TABLE, NOT A FLAG ON THE SPECIES.** ⚠️ The alternative — `carriable:
 * true` on `Species` — reads as though carrying were a property of the animal.
 * **It is a property of the PLAYER'S HANDS**, and the day a wheelbarrow or a
 * bigger settler exists the answer changes without the sheep changing at all.
 *
 * ⚠️ **A kind that is not in here cannot be carried**, rather than being
 * carriable by default. **That is `spear.ts`'s rule pointed the other way**: it
 * ignores anything that is not a fish so that a sheep never became spearable by
 * omission — and this ignores anything that is not listed so **a whale never
 * becomes liftable by omission.**
 */
const CARRIABLE = new Map<string, { readonly radius: number }>([
  /* 🐑 *kind* is Icelandic for a sheep — the same convention as `þorskur` and
     `bleikja`, and it reads like a copy-paste bug every time somebody new sees
     `kind: 'kind'`. It is not one. */
  ['kind', { radius: SHEEP_LENGTH * 0.5 }],
]);

/** Can this species be picked up by hand at all? */
export function isCarriable(kind: string): boolean {
  return CARRIABLE.has(kind);
}

/**
 * 🙌 **The creature under your crosshair that you could pick up, or
 * `undefined`.**
 *
 * **Both ends run this, on the same numbers** — the client to decide whether to
 * offer *"pick up"*, the server to decide whether the id it was handed was
 * honest. 🔴 **The id on the wire is a PROPOSAL, never a fact**, the same rule
 * the spear keeps and the same rule the bug reporter's byline was built on:
 * *the browser never sends a NAME, it sends a credential.*
 *
 * ⚠️ **`y` on the wire is the FEET.** A sheep is 0.85 m tall, which is far more
 * than the aim radius forgives, so **the target is lifted to the middle of the
 * body** before the geometry runs. 🔴 **Without that lift you would have to aim
 * at the GROUND under a sheep to pick it up**, which is precisely the class of
 * half-metre fault this project has shipped twice — *measure the thing under
 * the thing.*
 */
export function carryTargetFrom(
  eye: Vec3,
  look: Vec3,
  targets: readonly AimTarget[],
  reach: number = CARRY_REACH,
): AimTarget | undefined {
  const lifted = targets.map((t) =>
    t.kind === 'kind' ? { ...t, y: t.y + SHEEP_HEIGHT * 0.5 } : t,
  );
  const hit = aimAt(eye, look, lifted, reach, (t) => CARRIABLE.get(t.kind)?.radius ?? 0);
  if (hit === undefined) return undefined;
  /* ⚠️ Hand back the ORIGINAL row, not the lifted copy — the caller wants the
     creature's real position and its id, not our aiming fiction. */
  return targets.find((t) => t.id === hit.id);
}

/**
 * ⏱️ **How often a kept creature's position is written down.**
 *
 * ⚠️ **A kept sheep WANDERS — D123 says so** (*"they wander unless they are in a
 * pen"*) — **so its position changes ten times a second, for ever.** Writing
 * each beat would be ~36,000 rows an hour **per sheep**, which is
 * `positionrule.ts`'s own arithmetic arriving at a second subject.
 *
 * 🔴 **THIRTY SECONDS, NOT FIFTEEN.** A player's position is worth more than a
 * sheep's: losing thirty seconds of a sheep's wandering puts it a few metres
 * from where it was, **inside its own wander radius of 10 m**, which is to say
 * nowhere a person would notice.
 */
export const CREATURE_SAVE_INTERVAL_MS = 30_000;

/**
 * 📏 **How far a kept creature must have moved for a save to be worth doing.**
 *
 * ⚠️ **Standing still must cost nothing.** A penned sheep that has settled would
 * otherwise write two rows a minute for ever, and a pen is the whole point of
 * D123.
 */
export const CREATURE_SAVE_MIN_METRES = 2;

/** What was last written down for this creature, or `null` if nothing has been. */
export interface LastCreatureSave {
  readonly at: Vec3;
  readonly ms: number;
}

/**
 * 💾 **Is it worth writing this creature's position down?**
 *
 * 🔴 **Pure, and in `shared/` rather than beside the socket, for the reason
 * `positionrule.ts` states in its own header:** *if a decision can be arithmetic
 * over plain values, put it where a test can reach it.* ⚠️ **`server/index.ts`
 * cannot be imported without a database**, so a rule written there has no test
 * on this workstation at all — **which is exactly how the `players` table came
 * to hold zero rows for weeks.**
 *
 * ⚠️ **BOTH conditions, not either** — long enough ago **and** far enough away.
 */
export function shouldSaveCreature(
  last: LastCreatureSave | null,
  at: Vec3,
  nowMs: number,
): boolean {
  /* 🔴 The first write of a session is always worth doing: it is the one that
     creates the row, and until it lands nothing else about this creature can
     be stored. The same reason `shouldSavePosition` gives. */
  if (last === null) return true;
  if (nowMs - last.ms < CREATURE_SAVE_INTERVAL_MS) return false;
  const moved = Math.hypot(at.x - last.at.x, at.y - last.at.y, at.z - last.at.z);
  return moved >= CREATURE_SAVE_MIN_METRES;
}

/**
 * 🙌 **Where a carried creature is set down**, given where the player stands and
 * which way they face.
 *
 * 🔴 **IN FRONT OF THEM, NOT UNDER THEIR FEET.** A sheep put down inside the
 * player is a sheep inside a solid thing, and layer ② would then have to push it
 * out — which is the *"animal is inside something"* fault
 * `14_The_Animal_System.md` warns about, arriving by our own hand.
 *
 * ⚠️ **The CALLER must still check the spot is clear** with the species' own
 * `fits`. **This returns a proposal, not a promise** — the same shape as
 * everything else a client asks for.
 */
export function setDownAt(at: Vec3, look: Vec3): Vec3 {
  /* ⚠️ Flattened: you may be looking at the sky or at your boots, and neither
     is a place to put a sheep. A sheep goes on the GROUND in the direction you
     face, and the ground decides its height — never this function. */
  const flat = Math.hypot(look.x, look.z);
  if (flat < 1e-6) {
    /* Looking straight up or down. Put it beside you rather than inside you. */
    return { x: at.x + CARRY_PLACE_DISTANCE, y: at.y, z: at.z };
  }
  return {
    x: at.x + (look.x / flat) * CARRY_PLACE_DISTANCE,
    y: at.y,
    z: at.z + (look.z / flat) * CARRY_PLACE_DISTANCE,
  };
}

/**
 * 📏 **How far in front of you a carried creature is set down, in metres.**
 *
 * ⚠️ **Clear of your own footprint and inside {@link CARRY_REACH}**, so the
 * sheep you just put down is one you could immediately pick up again. **A
 * distance greater than the reach would let you post a sheep through a wall.**
 */
export const CARRY_PLACE_DISTANCE = 1.4;
